"use client";

import React, { useState, useEffect } from "react";
import Image, { ImageProps } from "next/image";

export interface SmoothImageProps extends ImageProps {
  containerClassName?: string;
  lowResSize?: string;
}

export default function SmoothImage({
  src,
  alt,
  className = "",
  containerClassName = "",
  fill,
  onLoad,
  style,
  lowResSize = "l",
  ...props
}: SmoothImageProps) {
  const [highResLoaded, setHighResLoaded] = useState(false);
  const [lowResLoaded, setLowResLoaded] = useState(false);

  const srcString =
    typeof src === "string"
      ? src
      : typeof src === "object" && src !== null && "src" in src
        ? (src as { src: string }).src
        : "";

  // Check if URL specifies 2K quality (&size= with no size suffix)
  const is2kImage = Boolean(
    srcString &&
      (srcString.endsWith("&size=") || /&size=(&|$)/.test(srcString)),
  );

  // Generate low-res preview URL (size=l) for 2K images
  const lowResSrc = is2kImage
    ? srcString.replace("&size=", `&size=${lowResSize}`)
    : null;

  const wrapperStyle = fill
    ? "relative w-full h-full overflow-hidden"
    : "relative overflow-hidden inline-block";

  useEffect(() => {
    setHighResLoaded(false);
    setLowResLoaded(false);
  }, [srcString]);

  return (
    <div className={`${wrapperStyle} ${containerClassName}`}>
      {/* 1. Skeleton Shimmer while initial load is in progress */}
      {!lowResLoaded && !highResLoaded && (
        <div className="absolute inset-0 bg-black/10 dark:bg-white/5 animate-pulse z-[1] pointer-events-none" />
      )}

      {/* 2. Low-res (size=l) blurred preview image */}
      {lowResSrc && !highResLoaded && (
        <Image
          src={lowResSrc}
          alt={alt}
          fill={fill}
          style={style}
          className={`transition-all duration-500 ease-out filter blur-md scale-[1.03] ${
            lowResLoaded ? "opacity-100" : "opacity-0"
          } ${className}`}
          onLoad={() => setLowResLoaded(true)}
          {...props}
        />
      )}

      {/* 3. Main 2K high-resolution image */}
      <Image
        src={src}
        alt={alt}
        fill={fill}
        style={style}
        className={`transition-all duration-700 ease-out ${
          highResLoaded
            ? "opacity-100 scale-100 blur-0"
            : lowResSrc
              ? "opacity-0"
              : lowResLoaded
                ? "opacity-100 blur-0"
                : "opacity-0 scale-[1.02] blur-sm"
        } ${className}`}
        onLoad={(e) => {
          setHighResLoaded(true);
          if (onLoad) onLoad(e);
        }}
        {...props}
      />
    </div>
  );
}
